import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,

  Alert,
  CircularProgress,
  Grid,
  Typography,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  SelectChangeEvent,
  Popover
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Router as RouterIcon,
  Map as MapIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { ftthCaixaService, FtthCaixa } from '../../services/ftthCaixaService';
import { MapSelectorModal } from '../common';
import EscritorioSelect from '../selects/EscritorioSelect';
import PonPortsOltSelect from '../selects/PonPortsOltSelect';
import { api } from '../../services/api';
import { CAIXA_DEFAULT_ICON_ID } from '../../utils/mapPinIcons';

interface FtthCaixaCtoFormProps {
  onSuccess?: (caixa: FtthCaixa) => void;
  onCancel?: () => void;
  caixa?: FtthCaixa; // Para edição - recebe dados completos
  coordinates?: { lat: number; lng: number };
  isFullscreen?: boolean;
}

interface FtthCaixaCtoFormData {
  name: string;
  address: string;
  latitude: string;
  longitude: string;
  olt_id: number | '';
  port_id: number | '';
  capacidade: number | '';
  status: 'ativo' | 'inativo' | 'aguardando_instalacao' | 'manutencao';
  tipo_rede: 'propria' | 'neutra';
  operadora: string;
  color: string;
  color_text: string;
  escritorio_id: number;
}

// Paleta de cores para seleção no formulário
const COLOR_PALETTE = [
  '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
  '#FFD700', '#FFA500', '#FF6347', '#DC143C', '#8B0000', '#006400', '#228B22', '#4169E1',
  '#4B0082', '#800080', '#808080', '#A9A9A9', '#D3D3D3', '#FFE4B5', '#F0E68C', '#98FB98',
  '#87CEEB', '#DDA0DD', '#F5DEB3', '#DEB887', '#BC8F8F', '#2F4F4F', '#696969', '#C0C0C0',
];

const FtthCaixaCtoForm: React.FC<FtthCaixaCtoFormProps> = ({ onSuccess, onCancel, caixa, coordinates, isFullscreen }) => {
  const [formData, setFormData] = useState<FtthCaixaCtoFormData>({
    name: caixa?.name || '',
    address: caixa?.address || '',
    latitude: coordinates?.lat ? coordinates.lat.toString() : caixa?.latitude ? caixa.latitude.toString() : '',
    longitude: coordinates?.lng ? coordinates.lng.toString() : caixa?.longitude ? caixa.longitude.toString() : '',
    olt_id: (caixa as any)?.olt_id || caixa?.pon_port?.olt?.id || '',
    port_id: caixa?.port_id || '',
    capacidade: (caixa?.capacidade as number) || '',
    status: caixa?.status || 'ativo',
    tipo_rede: (caixa as any)?.tipo_rede || 'propria',
    operadora: (caixa as any)?.operadora || '',
    color: (caixa as any)?.color || '#FF0000',
    color_text: (caixa as any)?.color_text || '#FFFFFF',
    escritorio_id: (caixa as any)?.escritorio_id || 1,
  });

  const [olts, setOlts] = useState<any[]>([]);
  const [loadingOlts, setLoadingOlts] = useState(false);
  const [ponPortSelected, setPonPortSelected] = useState<number | ''>(caixa?.port_id || ''); // Corrigido: usar port_id
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{lat: number, lng: number} | null>(null);
  const [colorPickerAnchor, setColorPickerAnchor] = useState<{ el: HTMLElement; field: 'color' | 'color_text' } | null>(null);
  console.log('🔍 CaixaForm - coordinates:', coordinates);
  console.log('🔍 CaixaForm - caixa prop:', caixa);

  useEffect(() => {
    loadOlts();
    // Removido loadPonPorts() - não é necessário pois o PonPortsOltSelect busca automaticamente
  }, []);

  // Efeito para recarregar dados quando a caixa mudar (especialmente para edição)
  useEffect(() => {
    console.log('🔄 CaixaForm - caixa prop mudou:', caixa);
    
    if (caixa) {
      // Recarregar dados do formulário com os dados da caixa
      const oltId = (caixa as any)?.olt_id || caixa.pon_port?.olt?.id || '';
      
      setFormData({
        name: caixa.name || '',
        address: caixa.address || '',
        latitude: coordinates?.lat ? coordinates.lat.toString() : caixa.latitude ? caixa.latitude.toString() : '',
        longitude: coordinates?.lng ? coordinates.lng.toString() : caixa.longitude ? caixa.longitude.toString() : '',
        olt_id: oltId,
        port_id: caixa.port_id || '',
        capacidade: (caixa.capacidade as number) || '',
        status: caixa.status || 'ativo',
        tipo_rede: (caixa as any)?.tipo_rede || 'propria',
        operadora: (caixa as any)?.operadora || '',
        color: (caixa as any)?.color || '#FF0000',
        color_text: (caixa as any)?.color_text || '#FFFFFF',
        escritorio_id: (caixa as any)?.escritorio_id || 1,
      });
      
      setPonPortSelected(caixa.port_id || '');
      
      console.log('✅ CaixaForm - dados recarregados para edição:', {
        olt_id: oltId,
        olt_id_direto: (caixa as any)?.olt_id,
        olt_id_relacionamento: caixa.pon_port?.olt?.id,
        port_id: caixa.port_id,
        pon_port: caixa.pon_port
      });
    }
  }, [caixa, coordinates]);

  const loadOlts = async () => {
    setLoadingOlts(true);
    try {
      const response = await api.get('/olts?limit=1000');
      let oltsArr = [];
      if (Array.isArray(response.data)) {
        oltsArr = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        oltsArr = response.data.data;
      } else if (response.data && response.data.success && Array.isArray(response.data.data)) {
        oltsArr = response.data.data;
      } else if (response.data && response.data.olts && Array.isArray(response.data.olts)) {
        oltsArr = response.data.olts;
      }
      setOlts(oltsArr);
    } catch (err) {
      setOlts([]);
    } finally {
      setLoadingOlts(false);
    }
  };

  const handleChange = (field: keyof FtthCaixaCtoFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSelectChange = (field: keyof FtthCaixaCtoFormData) => (event: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleOltChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value === '' ? '' : Number(event.target.value);
    setFormData(prev => ({
      ...prev,
      olt_id: value,
      port_id: '', // Corrigido: usar port_id
    }));
    setPonPortSelected('');
  };

  const handlePonPortChange = (value: string[]) => {
    // Só permite seleção única
    const selected = value.length > 0 ? value[0] : '';
    setPonPortSelected(selected ? Number(selected.split('/').pop()) : '');
    setFormData(prev => ({
      ...prev,
      port_id: selected ? Number(selected.split('/').pop()) : '', // Corrigido: usar port_id
    }));
  };

  const handleOpenMapModal = () => {
    // Se já tem coordenadas, usar como ponto inicial
    if (formData.latitude && formData.longitude) {
      setSelectedLocation({
        lat: parseFloat(formData.latitude),
        lng: parseFloat(formData.longitude)
      });
    } else {
      // Coordenadas padrão (centro do Brasil)
      setSelectedLocation({ lat: -14.235004, lng: -51.92528 });
    }
    setMapModalOpen(true);
  };

  const handleCloseMapModal = () => {
    setMapModalOpen(false);
  };

  const handleConfirmMapLocation = (lat: number, lng: number) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat.toString(),
      longitude: lng.toString()
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('Nome é obrigatório');
      return false;
    }
    if (!formData.address.trim()) {
      setError('Endereço é obrigatório');
      return false;
    }
    if (!formData.escritorio_id) {
      setError('Escritório é obrigatório');
      return false;
    }
    if (formData.tipo_rede === 'propria' && !formData.olt_id) {
      setError('OLT é obrigatório quando o tipo de rede é própria');
      return false;
    }
    if (formData.tipo_rede === 'propria' && !formData.port_id) {
      setError('Porta PON é obrigatória quando o tipo de rede é própria');
      return false;
    }
    if (formData.tipo_rede === 'neutra' && !formData.operadora.trim()) {
      setError('Operadora é obrigatória quando o tipo de rede é neutra');
      return false;
    }
    if (!formData.capacidade) {
      setError('Total de portas é obrigatório');
      return false;
    }
    const totalPortsNum = Number(formData.capacidade);
    if (totalPortsNum < 2 || totalPortsNum > 24) {
      setError('Total de portas deve ser entre 2 e 24');
      return false;
    }

    // Validar coordenadas (obrigatórias)
    if (!formData.latitude || !formData.longitude) {
      setError('Latitude e longitude são obrigatórias');
      return false;
    }

    const lat = parseFloat(formData.latitude);
    const lng = parseFloat(formData.longitude);
    
    if (isNaN(lat) || lat < -90 || lat > 90) {
      setError('Latitude deve estar entre -90 e 90');
      return false;
    }
    if (isNaN(lng) || lng < -180 || lng > 180) {
      setError('Longitude deve estar entre -180 e 180');
      return false;
    }

    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

          try {
      setLoading(true);

      const submitData = {
        name: formData.name.trim(),
        address: formData.address.trim(),
        latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
        longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
        olt_id: formData.olt_id || undefined,
        port_id: formData.port_id || undefined,
        capacidade: formData.capacidade as number,
        status: formData.status,
        tipo: 'cto' as const,
        tipo_rede: formData.tipo_rede,
        operadora: formData.tipo_rede === 'neutra' ? formData.operadora.trim() : undefined,
        color: formData.color.trim() || undefined,
        color_text: formData.color_text.trim() || undefined,
        icon: CAIXA_DEFAULT_ICON_ID,
        escritorio_id: formData.escritorio_id,
      };

      let response;
      if (caixa) {
        // Edição
        response = await ftthCaixaService.update(caixa.id, submitData);
      } else {
        // Criação
        response = await ftthCaixaService.create(submitData);
      }

      // Verificar se a operação foi bem-sucedida
      // A API pode retornar diretamente o objeto CAIXA ou um wrapper {success, data}
      const isSuccess = response && (
        response.success === true ||  // Wrapper format: {success: true, data: {...}}
        response.data ||              // Wrapper format with data
        ('id' in response && response.id) // Direct CAIXA object format: {id: 1, name: "...", ...}
      );
      
      console.log('🔄 Resposta da API:', response);
      console.log('✅ Operação bem-sucedida:', isSuccess);
      
      if (isSuccess && onSuccess) {
        console.log('📞 Chamando função de sucesso');
        // Se é um wrapper, usar response.data, senão usar response diretamente
        const caixaData = response.data || response;
        onSuccess(caixaData);
      } else if (!isSuccess) {
        console.warn('⚠️ Operação não foi bem-sucedida ou resposta inválida');
      } else if (!onSuccess) {
        console.warn('⚠️ Função onSuccess não fornecida');
      }
    } catch (err: any) {
      console.error('Erro ao salvar CAIXA:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao salvar CAIXA');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        {caixa ? 'Editar CTO' : 'Nova CTO'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Informações Básicas */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 0 }}>
            Informações Básicas
          </Typography>
        </Grid>

        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            label="Nome da CTO"
            value={formData.name}
            onChange={handleChange('name')}
            required
            size="small"
            placeholder="Ex: CTO-01 Bairro Norte"
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl 
            fullWidth 
            size="small" 
            required
            sx={{
              '& .MuiPopover-root': {
                zIndex: isFullscreen ? 10004 : 1500,
              }
            }}
          >
            <InputLabel>Total de Portas</InputLabel>
            <Select
              value={formData.capacidade}
              onChange={handleSelectChange('capacidade')}
              label="Total de Portas"
              MenuProps={{
                sx: {
                  zIndex: isFullscreen ? 10004 : 1500,
                }
              }}
            >
              <MenuItem value={2}>2 Portas</MenuItem>
              <MenuItem value={4}>4 Portas</MenuItem>
              <MenuItem value={8}>8 Portas</MenuItem>
              <MenuItem value={16}>16 Portas</MenuItem>
              <MenuItem value={24}>24 Portas</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl 
            fullWidth 
            size="small" 
            required
            sx={{
              '& .MuiPopover-root': {
                zIndex: isFullscreen ? 10004 : 1500,
              }
            }}
          >
            <InputLabel>Tipo de rede</InputLabel>
            <Select
              value={formData.tipo_rede}
              onChange={(e) => {
                const value = e.target.value as 'propria' | 'neutra';
                setFormData(prev => ({
                  ...prev,
                  tipo_rede: value,
                  ...(value === 'propria' ? { operadora: '' } : {}),
                }));
              }}
              label="Tipo de rede"
              MenuProps={{
                sx: {
                  zIndex: isFullscreen ? 10004 : 1500,
                }
              }}
            >
              <MenuItem value="propria">Própria</MenuItem>
              <MenuItem value="neutra">Neutra</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Operadora"
            value={formData.operadora}
            onChange={handleChange('operadora')}
            size="small"
            placeholder="Nome da operadora"
            required={formData.tipo_rede === 'neutra'}
            disabled={formData.tipo_rede === 'propria'}            
          />
        </Grid>

        {/* Integração com OLT */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
            Integração com OLT
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small" required={formData.tipo_rede === 'propria'}>
            <InputLabel>OLT</InputLabel>
            <Select
              value={formData.olt_id === '' ? '' : formData.olt_id.toString()}
              onChange={handleOltChange}
              label="OLT"
              disabled={loadingOlts}
            >
              <MenuItem value="">
                <em>Selecione uma OLT</em>
              </MenuItem>
              {olts.map((olt) => (
                <MenuItem key={olt.id} value={olt.id.toString()}>{olt.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <PonPortsOltSelect
            oltId={formData.olt_id || 0}
            value={ponPortSelected ? ponPortSelected.toString() : ''}
            onChange={(value) => {
              const selected = typeof value === 'string' ? value : value[0] || '';
              setPonPortSelected(selected ? Number(selected.split('/').pop()) : '');
              setFormData(prev => ({
                ...prev,
                port_id: selected ? Number(selected.split('/').pop()) : '',
              }));
            }}
            label="Porta PON"
            required={formData.tipo_rede === 'propria'}
            fullWidth
            size="small"
            disabled={!formData.olt_id}
            multiple={false}
          />
        </Grid>

        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1 }}>
          <Typography variant="subtitle2" color="primary">
            Localização              
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<MapIcon />}
            onClick={handleOpenMapModal}
          >
            Localização
          </Button>
        </Grid>

        
        {/* Coordenadas Geográficas */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Latitude"
            value={formData.latitude}
            onChange={handleChange('latitude')}
            size="small"
            type="number"
            required
            placeholder="-23.550520"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationIcon fontSize="small" />
                </InputAdornment>
              ),
              inputProps: { 
                step: "any",
                min: -90,
                max: 90
              }
            }}
            helperText="Latitude obrigatória"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Longitude"
            value={formData.longitude}
            onChange={handleChange('longitude')}
            size="small"
            type="number"
            required
            placeholder="-46.633309"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationIcon fontSize="small" />
                </InputAdornment>
              ),
              inputProps: { 
                step: "any",
                min: -180,
                max: 180
              }
            }}
            helperText="Longitude obrigatória"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Endereço"
            value={formData.address}
            onChange={handleChange('address')}
            required
            size="small"
            placeholder="Rua, número, bairro, cidade"            
          />
        </Grid>



        {/* Cor e ícone no mapa */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
            Personalização do ícone no mapa
          </Typography>
        </Grid>

        <Grid item xs={12}>          
          <Grid container spacing={2}>
            {/* Coluna 1: Cor do ícone e Cor do label */}
            <Grid item xs={12} sm={4} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Cor do ícone</Typography>
                  <Box
                    onClick={(e) => setColorPickerAnchor({ el: e.currentTarget, field: 'color' })}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: formData.color || '#ccc', border: '1px solid #999', flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{formData.color || '#FF0000'}</Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Cor do label</Typography>
                  <Box
                    onClick={(e) => setColorPickerAnchor({ el: e.currentTarget, field: 'color_text' })}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: formData.color_text || '#fff', border: '1px solid #999', flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{formData.color_text || '#FFFFFF'}</Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Popover
          open={!!colorPickerAnchor}
          anchorEl={colorPickerAnchor?.el}
          onClose={() => setColorPickerAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { zIndex: isFullscreen ? 10004 : 1500 } } }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {colorPickerAnchor?.field === 'color' ? 'Cor do ícone' : 'Cor do label'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: 208 }}>
              {COLOR_PALETTE.map((hex) => (
                <Box
                  key={hex}
                  onClick={() => {
                    if (colorPickerAnchor?.field) {
                      setFormData(prev => ({ ...prev, [colorPickerAnchor.field]: hex }));
                      setColorPickerAnchor(null);
                    }
                  }}
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: hex,
                    border: '1px solid',
                    borderColor: hex === '#FFFFFF' ? '#ccc' : 'transparent',
                    borderRadius: 0.5,
                    cursor: 'pointer',
                    '&:hover': { outline: '2px solid', outlineColor: 'primary.main' },
                  }}
                  title={hex}
                />
              ))}
            </Box>
          </Box>
        </Popover>

                 

        

        
      

        {/* Status */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 0, mt: 2 }}>
            Status
          </Typography>
        </Grid>
        {/* Escritório */}
        <Grid item xs={12} sm={6}>
           <EscritorioSelect
             value={formData.escritorio_id}
             onChange={(value) => setFormData(prev => ({ 
               ...prev, 
               escritorio_id: Number(value) || 1
             }))}
             fullWidth
             size="small"
             required
             label="Escritório"
             placeholder="Selecione um escritório"
             onlyActive={true}
             allowClear={false}
             multiple={false}
           />
         </Grid> 

        <Grid item xs={12} sm={6}>
          <FormControl 
            fullWidth 
            size="small" 
            required
            sx={{
              '& .MuiPopover-root': {
                zIndex: isFullscreen ? 10004 : 1500,
              }
            }}
          >
            <InputLabel>Status da CAIXA</InputLabel>
            <Select
              value={formData.status}
              onChange={handleSelectChange('status')}
              label="Status da CAIXA"
              MenuProps={{
                sx: {
                  zIndex: isFullscreen ? 10004 : 1500,
                }
              }}
            >
              <MenuItem value="ativo">Ativo</MenuItem>
              <MenuItem value="inativo">Inativo</MenuItem>
              <MenuItem value="aguardando_instalacao">Aguardando Instalação</MenuItem>
              <MenuItem value="manutencao">Manutenção</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Botões */}
      <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 3 }}>
        {onCancel && (
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? 'Salvando...' : (caixa ? 'Atualizar' : 'Criar CTO')}
        </Button>
      </Box>

      {/* Modal para Seleção de Coordenadas no Mapa */}
      <MapSelectorModal
        open={mapModalOpen}
        onClose={handleCloseMapModal}
        onConfirm={handleConfirmMapLocation}
        initialCoordinates={selectedLocation}
        title="📍 Selecionar Localização no Mapa"
        description="Clique no mapa para selecionar a localização da CAIXA"
      />
    </Box>
  );
};

export default FtthCaixaCtoForm; 